import { useEffect, useRef, useState } from "react";

/**
 * Auto-advance hook for Listen mode.
 *
 * When `enabled` is true and `isReveal` becomes true, counts down from
 * `delayMs` (default 3 s) and calls `onAdvance`. Resets whenever `isReveal`
 * flips back to false (e.g. a new round starts).
 *
 * Returns `secondsLeft` so the UI can display a live countdown.
 */
export function useListenAutoAdvance(
  enabled: boolean,
  isReveal: boolean,
  onAdvance: () => void,
  delayMs = 3000,
): { secondsLeft: number } {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  useEffect(() => {
    if (!enabled || !isReveal) {
      setSecondsLeft(0);
      return;
    }

    const steps = Math.ceil(delayMs / 1000);
    setSecondsLeft(steps);

    let remaining = steps;
    const interval = setInterval(() => {
      remaining -= 1;
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onAdvanceRef.current();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [enabled, isReveal, delayMs]);

  return { secondsLeft };
}
